import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import {
  evaluateRumError,
  isBotUserAgent,
  registerAuthenticatedSessionProbe,
  safeLocalStorage,
  toDatadogEnv,
} from 'shared-components';

type FailureReason = 'error' | 'abandoned' | 'other';

interface OperationOptions {
  operationKey?: string;
  context?: Record<string, unknown>;
  description?: string;
}

interface DatadogLogsShim {
  info: (message: string, context?: Record<string, unknown>) => void;
  warn: (message: string, context?: Record<string, unknown>) => void;
  error: (message: string, context?: Record<string, unknown>) => void;
}

interface DatadogRumShim {
  addError: (error: Error, context?: Record<string, unknown>) => void;
  startSessionReplayRecording: () => void;
  setViewName: (name: string) => void;
  startOperation: (name: string, options?: OperationOptions) => void;
  succeedOperation: (name: string, options?: OperationOptions) => void;
  failOperation: (name: string, failureReason: FailureReason, options?: OperationOptions) => void;
}

const SERVICE_NAME = 'kundenkonto-webadmin';
const SENSITIVE_PARAMS = ['username', 'email', 'search'];
const REDACTED_URL_RE = /\/api\/v1\/admin\/patients\/search/;
const SESSION_TOKEN_STORAGE_KEY = 'rx-token';

registerAuthenticatedSessionProbe(() => safeLocalStorage.getItem(SESSION_TOKEN_STORAGE_KEY) != null);

let initialized = false;

const isPlaceholder = (value: string): boolean => !value || value.startsWith('${');

const withRum = (fn: (rum: typeof datadogRum) => void): void => {
  if (!initialized) return;
  try {
    fn(datadogRum);
  } catch {
    /* noop */
  }
};

const withLogs = (fn: (logs: typeof datadogLogs) => void): void => {
  try {
    fn(datadogLogs);
  } catch {
    /* noop */
  }
};

const logsShim: DatadogLogsShim = {
  info: (message, context) => withLogs((logs) => logs.logger.info(message, context)),
  warn: (message, context) => withLogs((logs) => logs.logger.warn(message, context)),
  error: (message, context) => withLogs((logs) => logs.logger.error(message, context)),
};

const installDatadogLogsShim = (): void => {
  (window as unknown as { __DD_LOGS__: DatadogLogsShim }).__DD_LOGS__ = logsShim;
};

const patchConsole = (): void => {
  if ((console as unknown as Record<string, unknown>)['__DD_PATCHED__']) return;
  (console as unknown as Record<string, unknown>)['__DD_PATCHED__'] = true;

  const serialize = (args: unknown[]): string =>
    args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');

  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalError(...args);
    try {
      const message = serialize(args);
      const error = args[0] instanceof Error ? args[0] : undefined;
      datadogLogs.logger.error(message, error ? { error: { stack: error.stack } } : {});
    } catch {
      /* noop */
    }
  };

  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    originalWarn(...args);
    try {
      datadogLogs.logger.warn(serialize(args));
    } catch {
      /* noop */
    }
  };

  const originalLog = console.log.bind(console);
  console.log = (...args: unknown[]) => {
    originalLog(...args);
    try {
      datadogLogs.logger.info(serialize(args));
    } catch {
      /* noop */
    }
  };
};

const bridgeGaEventToDatadog = (name: string, context: Record<string, unknown>): void =>
  withRum((rum) => rum.addAction(name, { source: 'google_analytics', ...context }));

const setDatadogUser = (id: string): void => withRum((rum) => rum.setUser({ id }));

const clearDatadogUser = (): void => withRum((rum) => rum.clearUser());

const reportDatadogError = (error: string | Error | unknown, context?: Record<string, unknown>): void =>
  withRum((rum) => rum.addError(error, context));

const startSessionReplayRecording = (): void => withRum((rum) => rum.startSessionReplayRecording());

const initDatadog = (): void => {
  const globalWindow = window as unknown as Record<string, unknown>;
  globalWindow.__DD_RUM_CONFIG__ = {
    clientToken: process.env.DD_CLIENT_TOKEN as string,
    applicationId: process.env.DD_APP_ID as string,
    traceEnabled: process.env.DD_TRACE_ENABLED as string,
    env: process.env.DD_ENV as string,
    version: process.env.DD_VERSION as string,
  };

  const config = globalWindow.__DD_RUM_CONFIG__ as Record<string, string>;
  const ddEnv = config.env ? toDatadogEnv(config.env) : '';

  if (isPlaceholder(config.clientToken) || isPlaceholder(config.applicationId) || config.traceEnabled !== 'true') {
    return;
  }

  if (isBotUserAgent(navigator.userAgent)) {
    return;
  }

  // prettier-ignore
  const rumConfig: RumInitConfiguration = {
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: 'datadoghq.eu',
    service: SERVICE_NAME,
    env: ddEnv || 'localhost',
    version: config.version || 'dev-build',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    compressIntakeRequests: true,
    allowedTracingUrls: [/https:\/\/.*\.zurrose\.ch/, /https:\/\/.*\.bluecare\.ch/, /https:\/\/.*\.aspectra\.com/],
    defaultPrivacyLevel: 'mask',
    beforeSend: (event, context) => {
      if (event.type === 'error') {
        const extra = evaluateRumError({
          message: event.error.message,
          stack: event.error.stack,
          source: event.error.source,
          error: context && 'error' in context ? context.error : undefined,
          ownOrigin: window.location.origin,
          ownBundlePath: `${import.meta.env.BASE_URL}assets/`,
        });
        if (extra === false) return false;

        event.context = { ...event.context, ...extra };
        return true;
      }

      if (event.type === 'resource' && REDACTED_URL_RE.test(event.resource.url)) {
        const url = new URL(event.resource.url);
        for (const param of SENSITIVE_PARAMS) {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '***');
          }
        }
        event.resource.url = url.toString();
      }
      return true;
    },
  };

  datadogRum.init(rumConfig);
  initialized = true;

  const rumShim: DatadogRumShim = {
    addError: (error, context) => reportDatadogError(error, context),
    startSessionReplayRecording: () => startSessionReplayRecording(),
    setViewName: (name) => withRum((r) => r.setViewName(name)),
    startOperation: (name, options) => withRum((r) => r.startOperation(name, options)),
    succeedOperation: (name, options) => withRum((r) => r.succeedOperation(name, options)),
    failOperation: (name, reason, options) => withRum((r) => r.failOperation(name, reason, options)),
  };
  globalWindow.__DD_RUM__ = rumShim;
  installDatadogLogsShim();
  window.dispatchEvent(new Event('dd:rum-ready'));

  patchConsole();
  datadogRum.setGlobalContextProperty('team', 'promofarma');
  datadogRum.setGlobalContextProperty('component', 'frontend');
  datadogRum.setGlobalContextProperty('kind', 'deployment');
  datadogRum.setGlobalContextProperty('framework', 'React');
  datadogRum.setGlobalContextProperty('app', 'webadmin');
  datadogRum.startSessionReplayRecording();

  datadogLogs.init({
    clientToken: config.clientToken,
    site: 'datadoghq.eu',
    service: SERVICE_NAME,
    env: ddEnv || 'localhost',
    version: config.version || 'dev-build',
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
  });
};

export {
  bridgeGaEventToDatadog,
  clearDatadogUser,
  initDatadog,
  installDatadogLogsShim,
  patchConsole,
  reportDatadogError,
  setDatadogUser,
  startSessionReplayRecording,
};
